import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { getSafeReturnTo } from "@/lib/publicRoutes";
import { Brain, Eye, EyeOff } from "lucide-react";
import { AuthMarketingPanel } from "@/components/AuthMarketingPanel";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login, user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  // Captured once: the session_expired/verified effects below strip the query string via
  // replaceState, which would otherwise lose returnTo on the next render.
  const [returnTo] = useState(() =>
    getSafeReturnTo(new URLSearchParams(window.location.search).get("returnTo"))
  );

  // Already signed in (or the session recovered after a transient failure bounced us here):
  // go straight back instead of stranding an authenticated user on the login screen.
  useEffect(() => {
    if (user) {
      setLocation(returnTo);
    }
  }, [user, returnTo, setLocation]);

  // Check for email verification success, session expired, or password reset errors in URL
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const isVerified = queryParams.get("verified") === "true";
    const sessionExpired = queryParams.get("session_expired") === "1";

    if (isVerified) {
      window.history.replaceState(null, "", window.location.pathname);
      toast({
        title: "Email verified successfully!",
        description: "Your email has been verified. Please sign in to continue.",
      });
    }

    if (sessionExpired) {
      window.history.replaceState(null, "", window.location.pathname);
      toast({
        title: "Session expired",
        description: "Please sign in again to continue.",
        variant: "default",
      });
    }
    
    // Check for password reset errors in URL hash
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const error = hashParams.get("error");
    const errorCode = hashParams.get("error_code");
    const errorDescription = hashParams.get("error_description");

    if (error && errorCode) {
      // Clear the hash from URL
      window.history.replaceState(null, "", window.location.pathname);
      
      let errorMessage = "Password reset failed";
      if (errorCode === "otp_expired") {
        errorMessage = "The password reset link has expired. Please request a new one.";
      } else if (errorCode === "access_denied") {
        errorMessage = errorDescription 
          ? decodeURIComponent(errorDescription.replace(/\+/g, " "))
          : "Access denied. The password reset link may be invalid or expired.";
      } else {
        errorMessage = errorDescription 
          ? decodeURIComponent(errorDescription.replace(/\+/g, " "))
          : "An error occurred with the password reset link.";
      }

      toast({
        title: "Password Reset Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  }, [toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(username, password);
      setLocation(returnTo);
    } catch (error) {
      // Error is handled in AuthContext with toast
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="min-h-screen flex flex-col md:flex-row">
        <div className="md:w-1/2 bg-muted/30 border-b md:border-b-0 md:border-r">
          <AuthMarketingPanel />
        </div>
        <div className="md:w-1/2 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center">
            <div className="h-12 w-12 rounded-lg bg-primary flex items-center justify-center">
              <Brain className="h-6 w-6 text-primary-foreground" />
            </div>
          </div>
          <div>
            <CardTitle className="text-2xl">Welcome to DEEPLY SMART</CardTitle>
            <CardDescription>
              Sign in to your AI-powered knowledge platform
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username or email</Label>
              <Input
                id="username"
                type="text"
                autoComplete="username"
                placeholder="Enter your username or email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                data-testid="input-username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="flex w-full items-stretch rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-9 min-w-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-md text-muted-foreground hover:bg-muted hover:text-foreground sm:w-10"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="button-toggle-password"
                >
                  {showPassword ? <EyeOff className="h-4 w-4 shrink-0" /> : <Eye className="h-4 w-4 shrink-0" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
              data-testid="button-login"
            >
              {isLoading ? "Signing in..." : "Sign in"}
            </Button>
          </form>
          <div className="mt-4 space-y-2 text-center text-sm">
            <div>
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() => setLocation("/forgot-password")}
                data-testid="link-forgot-password"
              >
                Forgot password?
              </button>
            </div>
            <div>
            <span className="text-muted-foreground">Don't have an account? </span>
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() =>
                setLocation(
                  returnTo === "/"
                    ? "/signup"
                    : `/signup?returnTo=${encodeURIComponent(returnTo)}`
                )
              }
              data-testid="link-signup"
            >
              Sign up
            </button>
            </div>
            <div>
              <a href="/privacy" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                Privacy Policy
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
        </div>
      </div>
    </div>
  );
}
